import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import { acceptSession, clearSession, getSessionToken, TOKEN_KEY, LOGGED_OUT_KEY } from '../utils/authSession';
import { BACKEND_URL } from '../utils/constants';

const API = BACKEND_URL;

const UserContext = createContext(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const restoring = useRef(false);
  const generation = useRef(0);
  const retryRef = useRef(null);

  const checkAuth = useCallback(async (background = false) => {
    if (restoring.current) return;
    restoring.current = true;
    if (background !== true) setLoading(true);
    const token = getSessionToken();
    const started = generation.current;
    try {
      if (!token && localStorage.getItem(LOGGED_OUT_KEY) === 'true') {
        setUser(null);
        setIsLoggedIn(false);
        setSessionError(null);
        return;
      }
      const recover = () => axios.get(`${API}/api/auth/restore`, { headers: { 'X-MyGoal-Client': '1' }, timeout: 15000, withCredentials: true });
      let recovered = !token;
      let res;
      if (!token) res = await recover();
      else {
        try { res = await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000, withCredentials: true }); }
        catch (error) {
          if (error.response?.status !== 401 || generation.current !== started || getSessionToken() !== token) throw error;
          recovered = true; res = await recover();
        }
      }
      // A logout or new login during this request must not restore the old account.
      if (generation.current !== started || getSessionToken() !== token) return;
      const profile = recovered ? acceptSession(res.data) : res.data;
      setUser(profile);
      localStorage.setItem('mygoal-user-id', res.data.id);
      setIsLoggedIn(true);
      setSessionError(null);
    } catch (e) {
      if (generation.current !== started || getSessionToken() !== token) return;
      if (e.response?.status === 401) {
        clearSession();
        setUser(null);
        setIsLoggedIn(false);
        setSessionError(null);
      } else {
        // Offline or an unavailable server is not an invalid login.
        setSessionError(token ? 'Unable to connect. Your login is saved. Please try again when you are online.' : 'Unable to restore your login. Please try again when you are online.');
      }
    } finally {
      restoring.current = false;
      if (generation.current !== started && getSessionToken()) {
        Promise.resolve().then(() => retryRef.current());
      } else setLoading(false);
    }
  }, []);
  retryRef.current = checkAuth;

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!sessionError) return;
    const retry = () => checkAuth();
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    return () => {
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
    };
  }, [sessionError, checkAuth]);

  useEffect(() => {
    // Reopening a suspended homescreen window validates and renews its session.
    const resume = () => { if (getSessionToken()) checkAuth(true); };
    const visible = () => { if (document.visibilityState === 'visible') resume(); };
    window.addEventListener('pageshow', resume);
    window.addEventListener('focus', resume);
    document.addEventListener('visibilitychange', visible);
    return () => { window.removeEventListener('pageshow', resume); window.removeEventListener('focus', resume); document.removeEventListener('visibilitychange', visible); };
  }, [checkAuth]);

  useEffect(() => {
    const reset = () => { generation.current += 1; setUser(null); setIsLoggedIn(false); setSessionError(null); };
    const onStorage = event => {
      if (event.key === TOKEN_KEY || event.key === null) {
        reset();
        if (getSessionToken()) checkAuth();
      }
    };
    window.addEventListener('mygoal-session-cleared', reset);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('mygoal-session-cleared', reset);
      window.removeEventListener('storage', onStorage);
    };
  }, [checkAuth]);

  const login = async (userId) => {
    try {
      const res = await axios.get(`${API}/api/auth/me`);
      const profile = res.data.access_token ? acceptSession(res.data) : res.data;
      if (userId && profile.id !== userId) throw new Error("Sign in with this account’s password");
      setUser(profile);
      localStorage.setItem('mygoal-user-id', res.data.id);
      setIsLoggedIn(true);
      setSessionError(null);
      return res.data;
    } catch (e) {
      console.error("Error logging in:", e);
      throw e;
    }
  };

  const loginWithCredentials = async (email, password) => {
    generation.current += 1;
    try {
      const res = await axios.post(`${API}/api/login`, { email, password });
      const profile = acceptSession(res.data);
      setUser(profile);
      localStorage.setItem('mygoal-user-id', res.data.id);
      setIsLoggedIn(true);
      setSessionError(null);
      return profile;
    } catch (e) {
      console.error("Error logging in:", e);
      throw e;
    }
  };

  const logout = () => {
    generation.current += 1;
    axios.post(`${API}/api/auth/logout`, {}, { headers: { Authorization: `Bearer ${getSessionToken()}` } }).catch(() => {});
    clearSession();
    setUser(null);
    setIsLoggedIn(false);
    setSessionError(null);
  };

  const updateUser = async (updates) => {
    if (!user) return;
    try {
      const res = await axios.put(`${API}/api/profile/${user.id}`, updates);
      setUser(res.data);
      return res.data;
    } catch (e) {
      console.error("Error updating user:", e);
      throw e;
    }
  };

  const createAccount = async (data) => {
    generation.current += 1;
    try {
      const res = await axios.post(`${API}/api/profile`, data);
      const profile = acceptSession(res.data);
      setUser(profile);
      localStorage.setItem('mygoal-user-id', res.data.id);
      setIsLoggedIn(true);
      setSessionError(null);
      return profile;
    } catch (e) {
      console.error("Error creating account:", e);
      throw e;
    }
  };

  const switchUser = async (userId) => {
    if (userId !== user?.id) throw new Error("Sign out and sign in to switch accounts");
    try {
      const res = await axios.get(`${API}/api/auth/me`);
      setUser(res.data);
      localStorage.setItem('mygoal-user-id', res.data.id);
      return res.data;
    } catch (e) {
      console.error("Error switching user:", e);
      throw e;
    }
  };

  return (
    <UserContext.Provider value={{ 
      user, 
      setUser, 
      loading, 
      isLoggedIn,
      sessionError,
      retrySession: checkAuth,
      login,
      loginWithCredentials,
      logout,
      updateUser, 
      createAccount, 
      switchUser, 
      isCoach: user?.account_type === 'coach',
      isClub: user?.account_type === 'club'
    }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
